/**
 * React Hooks for AudioPod SDK
 * Easy-to-use React hooks for AudioPod functionality
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { AudioPodClient } from '../../client/AudioPodClient';
import {
  UseAudioPodOptions,
  UseVoiceCloningOptions,
  UseMusicGenerationOptions,
  UseTranscriptionOptions,
  JobStatus,
  JobProgress,
  AudioPodError,
  VoiceCloneRequest,
  VoiceCloneResult,
  MusicGenerationRequest,
  MusicGenerationResult,
  TranscriptionRequest,
  TranscriptionResult,
  CreditInfo
} from '../../types';

/**
 * Base AudioPod hook
 */
export function useAudioPod(apiKey?: string, options: UseAudioPodOptions = {}) {
  const [client, setClient] = useState<AudioPodClient | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<AudioPodError | null>(null);

  useEffect(() => {
    if (apiKey) {
      try {
        const audioPodClient = new AudioPodClient({ apiKey });
        setClient(audioPodClient);
        setIsInitialized(true);
        setError(null);
      } catch (err) {
        setError(err as AudioPodError);
        setIsInitialized(false);
      }
    }
  }, [apiKey]);

  return {
    client,
    isInitialized,
    error
  };
}

/**
 * Voice cloning hook
 */
export function useVoiceCloning(apiKey?: string, options: UseVoiceCloningOptions = {}) {
  const { client, isInitialized } = useAudioPod(apiKey, options);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<VoiceCloneResult | null>(null);
  const [error, setError] = useState<AudioPodError | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const cloneVoice = useCallback(async (request: VoiceCloneRequest) => {
    if (!client) {
      setError({ message: 'AudioPod client not initialized', code: 'CLIENT_NOT_INITIALIZED' });
      return;
    }

    setIsLoading(true);
    setError(null);
    setProgress(0);
    setResult(null);

    try {
      const response = await client.voice.cloneVoice({
        ...request,
        waitForCompletion: false
      });

      if (options.onProgress) {
        options.onProgress({
          jobId: response.job.id,
          status: response.job.status,
          progress: response.job.progress || 0
        });
      }

      // Start polling for progress
      if (response.job.status !== JobStatus.COMPLETED) {
        pollIntervalRef.current = setInterval(async () => {
          try {
            const jobStatus = await client.voice.getJobStatus(response.job.id);
            setProgress(jobStatus.progress || 0);

            if (options.onProgress) {
              options.onProgress({
                jobId: jobStatus.id,
                status: jobStatus.status,
                progress: jobStatus.progress || 0
              });
            }

            if (jobStatus.status === JobStatus.COMPLETED) {
              const finalResult = { job: jobStatus, outputUrl: jobStatus.result?.output_url };
              setResult(finalResult);
              setIsLoading(false);
              setProgress(100);

              if (options.onSuccess) {
                options.onSuccess(finalResult);
              }

              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
              }
            } else if (jobStatus.status === JobStatus.FAILED) {
              const errorMsg = jobStatus.errorMessage || 'Voice cloning failed';
              setError({ message: errorMsg, code: 'PROCESSING_FAILED' });
              setIsLoading(false);

              if (options.onError) {
                options.onError({ message: errorMsg, code: 'PROCESSING_FAILED' });
              }

              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
              }
            }
          } catch (pollError) {
            setError(pollError as AudioPodError);
            setIsLoading(false);

            if (options.onError) {
              options.onError(pollError as AudioPodError);
            }

            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
            }
          }
        }, options.pollInterval || 5000);
      } else {
        // Job already completed
        setResult(response);
        setIsLoading(false);
        setProgress(100);

        if (options.onSuccess) {
          options.onSuccess(response);
        }
      }
    } catch (err) {
      const error = err as AudioPodError;
      setError(error);
      setIsLoading(false);

      if (options.onError) {
        options.onError(error);
      }
    }
  }, [client, options]);

  const cancel = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  return {
    cloneVoice,
    cancel,
    isLoading,
    progress,
    result,
    error,
    isInitialized
  };
}

/**
 * Music generation hook
 */
export function useMusicGeneration(apiKey?: string, options: UseMusicGenerationOptions = {}) {
  const { client, isInitialized } = useAudioPod(apiKey, options);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<MusicGenerationResult | null>(null);
  const [error, setError] = useState<AudioPodError | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const generateMusic = useCallback(async (request: MusicGenerationRequest) => {
    if (!client) {
      setError({ message: 'AudioPod client not initialized', code: 'CLIENT_NOT_INITIALIZED' });
      return;
    }

    setIsLoading(true);
    setError(null);
    setProgress(0);
    setResult(null);

    try {
      const response = await client.music.generateMusic({
        ...request,
        waitForCompletion: false
      });

      // Start polling for progress
      if (response.job.status !== JobStatus.COMPLETED) {
        pollIntervalRef.current = setInterval(async () => {
          try {
            const musicJob = await client.music.getJobStatus(response.job.id);
            setProgress(musicJob.progress || 0);

            if (options.onProgress) {
              options.onProgress({
                jobId: musicJob.id,
                status: musicJob.status,
                progress: musicJob.progress || 0
              });
            }

            if (musicJob.status === JobStatus.COMPLETED) {
              setResult({ job: musicJob });
              setIsLoading(false);
              setProgress(100);

              if (options.onSuccess) {
                options.onSuccess({ job: musicJob });
              }

              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
              }
            } else if (musicJob.status === JobStatus.FAILED) {
              const errorMsg = musicJob.errorMessage || 'Music generation failed';
              setError({ message: errorMsg, code: 'PROCESSING_FAILED' });
              setIsLoading(false);

              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
              }
            }
          } catch (pollError) {
            setError(pollError as AudioPodError);
            setIsLoading(false);

            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
            }
          }
        }, options.pollInterval || 5000);
      }
    } catch (err) {
      const error = err as AudioPodError;
      setError(error);
      setIsLoading(false);

      if (options.onError) {
        options.onError(error);
      }
    }
  }, [client, options]);

  const cancel = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  return {
    generateMusic,
    cancel,
    isLoading,
    progress,
    result,
    error,
    isInitialized
  };
}

/**
 * Transcription hook
 */
export function useTranscription(apiKey?: string, options: UseTranscriptionOptions = {}) {
  const { client, isInitialized } = useAudioPod(apiKey, options);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<TranscriptionResult | null>(null);
  const [error, setError] = useState<AudioPodError | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const transcribeAudio = useCallback(async (request: TranscriptionRequest) => {
    if (!client) {
      setError({ message: 'AudioPod client not initialized', code: 'CLIENT_NOT_INITIALIZED' });
      return;
    }

    setIsLoading(true);
    setError(null);
    setProgress(0);
    setResult(null);

    try {
      const response = await client.transcription.transcribeAudio({
        ...request,
        waitForCompletion: false
      });

      // Start polling for progress
      if (response.job.status !== JobStatus.COMPLETED) {
        pollIntervalRef.current = setInterval(async () => {
          try {
            const transcriptionJob = await client.transcription.getTranscriptionJob(response.job.id);
            setProgress(transcriptionJob.job.progress || 0);

            if (transcriptionJob.job.status === JobStatus.COMPLETED) {
              setResult(transcriptionJob);
              setIsLoading(false);
              setProgress(100);

              if (options.onSuccess) {
                options.onSuccess(transcriptionJob);
              }

              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
              }
            } else if (transcriptionJob.job.status === JobStatus.FAILED) {
              const errorMsg = transcriptionJob.job.errorMessage || 'Transcription failed';
              setError({ message: errorMsg, code: 'PROCESSING_FAILED' });
              setIsLoading(false);

              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
              }
            }
          } catch (pollError) {
            setError(pollError as AudioPodError);
            setIsLoading(false);

            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
            }
          }
        }, options.pollInterval || 5000);
      }
    } catch (err) {
      const error = err as AudioPodError;
      setError(error);
      setIsLoading(false);
    }
  }, [client, options]);

  const cancel = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsLoading(false);
  }, []);

  return {
    transcribeAudio,
    cancel,
    isLoading,
    progress,
    result,
    error,
    isInitialized
  };
}

/**
 * Credits hook
 */
export function useCredits(apiKey?: string) {
  const { client, isInitialized } = useAudioPod(apiKey);
  const [credits, setCredits] = useState<CreditInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AudioPodError | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!client) {
      setError({ message: 'AudioPod client not initialized', code: 'CLIENT_NOT_INITIALIZED' });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const creditInfo = await client.credits.getCreditBalance();
      setCredits(creditInfo);
    } catch (err) {
      setError(err as AudioPodError);
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (isInitialized) {
      fetchCredits();
    }
  }, [isInitialized, fetchCredits]);

  return {
    credits,
    isLoading,
    error,
    refetch: fetchCredits,
    isInitialized
  };
}
